import React, { useState, useEffect, useCallback } from "react";
import { getCertificates } from "../api/certificates_api";
import { verifyCertificateRecord } from "../crypto/verifyCertificate";

/* ═══════════════════════════════════════════════════════════════════════════
   GridMind — Certificate Registry

   The registry lives in the dark app shell.
   The certificate itself is paper — a document, not a panel.

   FILE:   src/pages/CertificateRegistry.jsx
   ROUTE:  <Route path="/certificates" element={<CertificateRegistry />} />
   DATA:   reads carbon.saving_certificates directly from Supabase
           via ../api/certificates.js — no backend server required.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── tokens ──────────────────────────────────────────────────────────────
   SHELL = your app. Swap these to your exact GridMind hexes.
   SHEET = the document. Deliberately paper.
   Both share the same accent family, so nothing clashes.            */
const S = {
  bg: "#0A0F0D",       // app background
  panel: "#111B17",    // raised surface
  ink: "#F1F5F2",      // primary text on dark
  muted: "#A4B8AE",    // readable secondary text
  faint: "#748C80",    // metadata and small labels
  rule: "#2B4036",     // visible but restrained separators
};

const P = {
  sheet: "#FBFBF9",    // the paper — warm-neutral, not cream
  ink: "#12201B",      // document ink
  muted: "#5E6E66",
  faint: "#98A69E",
  rule: "#E2E7E4",
  hair: "#EFF2F0",
};

/* shared accent family — identical across shell and sheet */
const A = {
  seal: "#1E7A4C",     // verified (sheet)
  sealLit: "#3FBF7F",  // verified (shell)
  oxide: "#B33A2B",    // declined (sheet)
  oxideLit: "#E5574B", // declined (shell)
  brass: "#A8801F",    // the floor (sheet)
  brassLit: "#D9A441", // the floor (shell)
};

const FONTS = `
@import url('https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,600&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
`;

const SERIF = "'Newsreader', Georgia, serif";
const SANS = "'IBM Plex Sans', system-ui, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";

/* ═══════════════════════════════════════════════════════════════════════════
   ADAPTER — maps certificate_job.py output to the UI shape.
   Adjust the right-hand side of each line if your JSON keys differ.
   ═══════════════════════════════════════════════════════════════════════════ */
const num = (v) =>
  v === null || v === undefined || v === "" || isNaN(Number(v)) ? null : Number(v);

/* diagnostics arrive as:
   { "placebo_in_space": { "value": "p = 0.040", "pass": true }, ... }        */
function normaliseDiagnostics(d) {
  const out = {};
  Object.entries(d || {}).forEach(([k, v]) => {
    const label = k
      .replace(/_/g, " ")
      .replace(/\bhhi\b/i, "HHI")
      .replace(/\b\w/g, (m) => m.toUpperCase());
    out[label] = {
      v: String((v && (v.value ?? v.v)) ?? v ?? "—"),
      pass: Boolean(v && (v.pass ?? v.passed)),
    };
  });
  return out;
}

/* Maps carbon.saving_certificates → the UI.
   Column names are exact; nothing here is a guess.                          */
function adapt(c) {
  const certified = c.status === "CERTIFIED";
  const effect = num(c.estimated_effect_kwh_day) ?? 0;

  return {
    raw: c,
    id: c.certificate_id,
    chainIndex: num(c.chain_index),
    feeder: c.feeder_id,
    region: c.region_key || "—",
    intervention: c.intervention || "—",
    commissioned: c.intervention_date || null,
    certified,
    declineReason: c.decline_reason,
    effect,
    mde: num(c.detectability_floor) ?? 0,
    ci: [num(c.ci_lower) ?? effect, num(c.ci_upper) ?? effect],
    donors: num(c.donors_used) ?? 0,
    unit: c.unit || "kWh/day",
    diagnostics: normaliseDiagnostics(c.diagnostics),
    prevHash: c.previous_hash,
    hash: c.record_hash,
    signature: c.signature,
    issued: c.issued_at,
    isSynthetic: c.is_synthetic !== false,
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   SIGNATURE ELEMENT — the detectability floor, made visible
   ═══════════════════════════════════════════════════════════════════════════ */
function ThresholdGauge({ effect, mde, ci, unit, certified }) {
  const span = Math.max(Math.abs(effect), mde, Math.abs(ci[1]), 1) * 1.4;
  const pct = (v) => `${Math.max(0, Math.min(100, (v / span) * 100))}%`;
  const accent = certified ? A.seal : A.oxide;

  return (
    <div>
      <div style={{ position: "relative", height: 106 }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: 30, height: 30, background: P.hair }} />

        <div
          style={{
            position: "absolute", left: 0, top: 30, height: 30,
            width: pct(Math.max(effect, 0)),
            background: certified
              ? "linear-gradient(90deg,#186340,#26925C)"
              : "linear-gradient(90deg,#8F2C20,#C4483A)",
            transition: "width 1.2s cubic-bezier(.16,1,.3,1)",
          }}
        />

        {/* confidence interval */}
        <div style={{
          position: "absolute", top: 70, height: 1,
          left: pct(Math.max(ci[0], 0)),
          width: `calc(${pct(ci[1])} - ${pct(Math.max(ci[0], 0))})`,
          background: P.faint,
        }} />
        <div style={{ position: "absolute", top: 66, left: pct(Math.max(ci[0], 0)), width: 1, height: 9, background: P.faint }} />
        <div style={{ position: "absolute", top: 66, left: pct(ci[1]), width: 1, height: 9, background: P.faint }} />
        <div style={{ position: "absolute", top: 84, left: 0, fontFamily: MONO, fontSize: 10, color: P.faint }}>
          95% CI&nbsp; {ci[0]} – {ci[1]}
        </div>

        {/* ── THE FLOOR ── */}
        <div style={{
          position: "absolute", left: pct(mde), top: 8, height: 62,
          width: 2, background: A.brass,
        }} />
        <div style={{
          position: "absolute", left: pct(mde), top: 0, transform: "translateX(-50%)",
          fontFamily: MONO, fontSize: 9.5, fontWeight: 600, color: A.brass,
          letterSpacing: "0.1em", whiteSpace: "nowrap",
        }}>
          FLOOR {mde}
        </div>

        <div style={{
          position: "absolute", left: `calc(${pct(Math.max(effect, 0))} + 12px)`, top: 32,
          fontFamily: MONO, fontSize: 21, fontWeight: 600, color: accent, whiteSpace: "nowrap",
        }}>
          {effect}
          <span style={{ fontSize: 11, color: P.muted, fontWeight: 400, marginLeft: 5 }}>{unit}</span>
        </div>
      </div>

      <p style={{
        margin: "14px 0 0", fontFamily: SANS, fontSize: 13,
        color: certified ? A.seal : A.oxide, lineHeight: 1.6,
      }}>
        {certified
          ? "The estimated effect clears the detectability floor under the configured certification criteria."
          : "The estimated effect falls below the detectability floor. The available evidence cannot support a claim of this size, so certification is declined."}
      </p>
    </div>
  );
}

/* ── the seal, pressed into the paper ───────────────────────────────────── */
function Seal({ certified }) {
  const c = certified ? A.seal : A.oxide;
  return (
    <div style={{
      width: 110, height: 110, borderRadius: "50%", border: `2px solid ${c}`,
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", position: "relative", flexShrink: 0,
    }}>
      <div style={{ position: "absolute", inset: 6, borderRadius: "50%", border: `1px solid ${c}`, opacity: 0.3 }} />
      <div style={{
        fontFamily: SERIF, fontSize: certified ? 16 : 14, fontWeight: 600, color: c,
        letterSpacing: "0.1em", lineHeight: 1.25, textAlign: "center",
      }}>
        {certified ? "CERTIFIED" : <>NOT<br />CERTIFIED</>}
      </div>
      <div style={{
        fontFamily: MONO, fontSize: 7.5, color: c, letterSpacing: "0.2em",
        marginTop: 6, opacity: 0.75,
      }}>
        GRIDMIND
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <div style={{
    fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.22em", color: P.faint,
    marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${P.rule}`,
  }}>
    {String(children).toUpperCase()}
  </div>
);

const Row = ({ k, v, mono }) => (
  <div style={{ display: "flex", padding: "10px 0", borderBottom: `1px solid ${P.hair}` }}>
    <div style={{ width: 176, flexShrink: 0, fontFamily: SANS, fontSize: 12, color: P.muted }}>{k}</div>
    <div style={{
      fontFamily: mono ? MONO : SANS, fontSize: mono ? 11.5 : 13,
      color: P.ink, wordBreak: "break-all", lineHeight: 1.5,
    }}>{v}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   THE DOCUMENT — paper, on the dark shell
   ═══════════════════════════════════════════════════════════════════════════ */
function Certificate({ r, onBack }) {
  const [verify, setVerify] = useState("idle");

  const [verifyDetails, setVerifyDetails] = useState(null);

  const runVerify = async () => {
    setVerify("checking");
    setVerifyDetails(null);
    try {
      const result = await verifyCertificateRecord(r.raw);
      setVerifyDetails(result);
      setVerify(result.valid ? "valid" : "invalid");
    } catch (error) {
      console.error("Certificate verification failed:", error);
      setVerifyDetails({ valid: false, hashValid: false, signatureValid: false, error: error.message });
      setVerify("unavailable");
    }
  };

  const label = {
    idle: "Verify cryptographic integrity",
    checking: "Verifying…",
    valid: "✓ Integrity Verified · Record Unaltered",
    invalid: "✕ Verification failed",
    unavailable: "Verifier offline",
  }[verify];

  const btnBg = verify === "valid" ? A.seal : verify === "invalid" ? A.oxide : P.ink;

  return (
    <div style={{ maxWidth: 880, margin: "0 auto" }}>
      <button
        onClick={onBack}
        style={{
          background: "none", border: "none", cursor: "pointer", padding: "6px 0",
          fontFamily: SANS, fontSize: 13, color: S.muted, marginBottom: 22,
        }}
      >
        ← Registry
      </button>

      {/* the sheet */}
      <article style={{
        background: P.sheet,
        boxShadow: "0 40px 90px -30px rgba(0,0,0,.75), 0 0 0 1px rgba(255,255,255,.04)",
      }}>
        <header style={{
          borderBottom: `3px double ${P.ink}`, padding: "30px 38px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 30,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.22em",
              color: P.faint, marginBottom: 12,
            }}>
              INTELLILEARN AI PRIVATE LIMITED · GRIDMIND
            </div>
            <h1 style={{
              margin: 0, fontFamily: SERIF, fontSize: 32, fontWeight: 500,
              color: P.ink, lineHeight: 1.15, letterSpacing: "-0.01em",
            }}>
              {r.isSynthetic ? <>Certificate of Simulated<br />Energy Impact</> : <>Certificate of Measured<br />Energy Impact</>}
            </h1>
            <div style={{
              marginTop: 14, fontFamily: MONO, fontSize: 12.5,
              color: A.brass, letterSpacing: "0.05em",
            }}>
              {r.id}
            </div>
          </div>
          <Seal certified={r.certified} />
        </header>

        <div style={{ padding: "0 38px 38px" }}>
          <section style={{ paddingTop: 28 }}>
            <Label>Subject</Label>
            <Row k="Feeder" v={r.feeder} mono />
            <Row k="Region" v={r.region} mono />
            <Row k="Intervention" v={r.intervention} />
            {r.commissioned && <Row k="Commissioned" v={r.commissioned} mono />}
            <Row k="Comparison pool" v={`${r.donors} donor feeders`} />
          </section>

          <section style={{ paddingTop: 32 }}>
            <Label>The finding</Label>
            <ThresholdGauge
              effect={r.effect} mde={r.mde} ci={r.ci} unit={r.unit} certified={r.certified}
            />
          </section>

          {!r.certified && (
            <section style={{
              marginTop: 26, padding: "20px 22px",
              background: "#FCF3F1", borderLeft: `3px solid ${A.oxide}`,
            }}>
              <div style={{
                fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.18em",
                color: A.oxide, marginBottom: 9,
              }}>
                NOT CERTIFIED
              </div>
              <div style={{
                fontFamily: MONO, fontSize: 13.5, fontWeight: 600,
                color: P.ink, marginBottom: 10,
              }}>
                {r.declineReason || "INSUFFICIENT_EVIDENCE"}
              </div>
              <p style={{ margin: 0, fontFamily: SANS, fontSize: 13, color: P.muted, lineHeight: 1.65 }}>
                {r.donors > 0 && r.donors < 20
                  ? `Only ${r.donors} donor feeders were available. The placebo test cannot reach significance below 1/(J+1) = ${(1 / (r.donors + 1)).toFixed(3)}, so no effect of any size is certifiable from this comparison pool. A minimum of 20 donors is required.`
                  : "The available data cannot support a claim of this size. No number is issued that the evidence does not justify."}
              </p>
            </section>
          )}

          {Object.keys(r.diagnostics).length > 0 && (
            <section style={{ paddingTop: 32 }}>
              <Label>Robustness</Label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: "0 36px" }}>
                {Object.entries(r.diagnostics).map(([k, d]) => (
                  <div key={k} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    padding: "10px 0", borderBottom: `1px solid ${P.hair}`,
                  }}>
                    <span style={{ fontFamily: SANS, fontSize: 12.5, color: P.ink }}>{k}</span>
                    <span style={{
                      fontFamily: MONO, fontSize: 11.5, marginLeft: 12, whiteSpace: "nowrap",
                      color: d.pass ? A.seal : A.oxide,
                    }}>
                      {d.v} {d.pass ? "✓" : "✕"}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section style={{ paddingTop: 32 }}>
            <Label>Provenance</Label>
            {r.issued && <Row k="Issued" v={new Date(r.issued).toUTCString()} mono />}
            <Row k="Previous record" v={r.prevHash} mono />
            <Row k="Record hash (SHA-256)" v={r.hash} mono />
            <Row k="Signature (Ed25519)" v={r.signature} mono />
            <Row
              k="Data provenance"
              mono
              v={
                <span style={{ color: r.isSynthetic ? A.brass : A.seal, fontWeight: 500 }}>
                  {r.isSynthetic
                    ? "SYNTHETIC — not measured from a live feeder"
                    : "MEASURED — live feeder telemetry"}
                </span>
              }
            />
          </section>

          <section style={{
            marginTop: 30, paddingTop: 24, borderTop: `1px solid ${P.rule}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 20, flexWrap: "wrap",
          }}>
            <p style={{ margin: 0, maxWidth: 460, fontFamily: SANS, fontSize: 12.5, color: P.muted, lineHeight: 1.65 }}>
              The integrity and issuer authenticity of this record can be independently verified using IntelliLearn AI's published public key.
            </p>
            <button
              onClick={runVerify}
              disabled={verify === "checking"}
              style={{
                fontFamily: SANS, fontSize: 13, fontWeight: 500, padding: "12px 24px",
                cursor: verify === "checking" ? "wait" : "pointer",
                border: `1px solid ${btnBg}`, background: btnBg, color: "#fff",
                letterSpacing: "0.02em", transition: "all .25s",
              }}
            >
              {label}
            </button>
          </section>
        </div>
      </article>

      {r.isSynthetic && (
        <p style={{
          marginTop: 18, fontFamily: SANS, fontSize: 12.5, color: S.muted,
          textAlign: "center", lineHeight: 1.65, maxWidth: 620, marginLeft: "auto", marginRight: "auto",
        }}>
          Issued from synthetic data. GridMind has not yet processed live feeder telemetry —
          every record is marked accordingly, and always will be.
        </p>
      )}
    </div>
  );
}

const Stat = ({ n, label, c }) => (
  <div>
    <div style={{ fontFamily: SERIF, fontSize: 32, fontWeight: 500, color: c, lineHeight: 1 }}>{n}</div>
    <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.18em", color: S.faint, marginTop: 6 }}>
      {String(label).toUpperCase()}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   REGISTRY — lives in the dark shell
   ═══════════════════════════════════════════════════════════════════════════ */
export default function CertificateRegistry() {
  const [records, setRecords] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const rows = await getCertificates();
      const list = rows.map(adapt);
      setRecords(list);
      setState(list.length ? "ready" : "empty");
    } catch (e) {
      setError(e.message || "Could not reach the certificate service");
      setState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const certified = records.filter((r) => r.certified).length;
  const notCertified = records.length - certified;
  const syntheticCount = records.filter((r) => r.isSynthetic).length;
  const liveCount = records.length - syntheticCount;
  const registryMode = syntheticCount === records.length
    ? "DEMONSTRATION REGISTRY · SYNTHETIC DATA · NO LIVE-FEEDER CLAIMS"
    : liveCount === records.length
    ? "LIVE IMPACT REGISTRY · FIELD-DATA RECORDS"
    : "MIXED REGISTRY · LIVE AND SYNTHETIC RECORDS";

  return (
    <>
      <style>{FONTS}</style>
      <div style={{ minHeight: "100vh", background: S.bg, padding: "52px 24px 90px" }}>
        {open ? (
          <Certificate r={open} onBack={() => setOpen(null)} />
        ) : (
          <div style={{ maxWidth: 880, margin: "0 auto" }}>
            <header style={{ marginBottom: 36 }}>
              <div style={{
                fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.22em",
                color: S.faint, marginBottom: 12,
              }}>
                APPEND-ONLY · HASH-CHAINED · TAMPER-EVIDENT
              </div>
              <h1 style={{
                margin: 0, fontFamily: SERIF, fontSize: 44, fontWeight: 500,
                color: S.ink, letterSpacing: "-0.015em",
              }}>
                Certificate Registry
              </h1>
              <p style={{
                margin: "14px 0 0", maxWidth: 580, fontFamily: SANS,
                fontSize: 14.5, color: S.muted, lineHeight: 1.7,
              }}>
                Every impact claim assessed by GridMind is permanently recorded — whether certified
                or not certified. Each record is cryptographically signed, linked to the preceding
                record, and independently verifiable using the issuer’s public key.
              </p>

              {state === "ready" && (
                <>
                  <div style={{ display: "flex", gap: 34, marginTop: 26, flexWrap: "wrap" }}>
                    <Stat n={certified} label="Certified" c={A.sealLit} />
                    <Stat n={notCertified} label="Not certified" c={A.oxideLit} />
                    <Stat n={records.length} label="Permanent records" c={A.brassLit} />
                  </div>
                  <div style={{ marginTop: 22, padding: "13px 16px", background: S.panel, border: `1px solid ${S.rule}`, borderLeft: `3px solid ${syntheticCount === records.length ? A.brassLit : A.sealLit}`, fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.12em", color: syntheticCount === records.length ? A.brassLit : A.sealLit, lineHeight: 1.5 }}>
                    {registryMode}
                  </div>
                </>
              )}
            </header>

            {state === "loading" && (
              <div style={{
                padding: "70px 0", textAlign: "center", fontFamily: MONO,
                fontSize: 12, color: S.faint, letterSpacing: "0.12em",
              }}>
                READING THE CHAIN…
              </div>
            )}

            {state === "error" && (
              <div style={{
                padding: 28, background: S.panel,
                border: `1px solid ${S.rule}`, borderLeft: `3px solid ${A.oxideLit}`,
              }}>
                <div style={{
                  fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.18em",
                  color: A.oxideLit, marginBottom: 10,
                }}>
                  CERTIFICATE SERVICE UNREACHABLE
                </div>
                <p style={{ margin: "0 0 18px", fontFamily: SANS, fontSize: 13.5, color: S.muted, lineHeight: 1.65 }}>
                  {error}. Check that REACT_APP_SUPABASE_URL and
                  REACT_APP_SUPABASE_ANON_KEY are set in your .env, and that the
                  carbon.saving_certificates table is reachable.
                </p>
                <button onClick={load} style={{
                  fontFamily: SANS, fontSize: 13, padding: "10px 20px", cursor: "pointer",
                  background: "transparent", color: S.ink, border: `1px solid ${S.rule}`,
                }}>
                  Try again
                </button>
              </div>
            )}

            {state === "empty" && (
              <div style={{
                padding: "60px 32px", background: S.panel,
                border: `1px solid ${S.rule}`, textAlign: "center",
              }}>
                <div style={{ fontFamily: SERIF, fontSize: 24, color: S.ink, marginBottom: 14 }}>
                  The chain is empty.
                </div>
                <p style={{
                  margin: "0 auto", maxWidth: 420, fontFamily: SANS,
                  fontSize: 13.5, color: S.muted, lineHeight: 1.65,
                }}>
                  No certificates have been issued yet. Run{" "}
                  <code style={{ fontFamily: MONO, color: A.brassLit }}>certificate_job.py</code>{" "}
                  to assess a feeder and issue the first record.
                </p>
              </div>
            )}

            {state === "ready" && (
              <>
                <div style={{ borderTop: `1px solid ${S.rule}` }}>
                  {records.map((r, i) => (
                    <button
                      key={r.id}
                      onClick={() => setOpen(r)}
                      style={{
                        width: "100%", textAlign: "left", background: "none", cursor: "pointer",
                        border: "none", borderBottom: `1px solid ${S.rule}`,
                        padding: "22px 6px", display: "flex", alignItems: "center", gap: 20,
                        transition: "background .18s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = S.panel)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                    >
                      <div style={{ fontFamily: MONO, fontSize: 11, color: S.faint, width: 28 }}>
                        {r.chainIndex != null ? `#${r.chainIndex}` : String(i + 1).padStart(2, "0")}
                      </div>
                      <div style={{
                        width: 4, height: 42, flexShrink: 0,
                        background: r.certified ? A.sealLit : A.oxideLit,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: MONO, fontSize: 13, color: S.ink, marginBottom: 5 }}>
                          {r.feeder}
                        </div>
                        <div style={{ fontFamily: SANS, fontSize: 12.5, color: S.muted }}>
                          {r.intervention} · {r.donors} donors
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{
                          fontFamily: MONO, fontSize: 16, marginBottom: 4,
                          color: r.certified ? A.sealLit : A.oxideLit,
                        }}>
                          {r.certified ? r.effect : "—"}
                          {r.certified && (
                            <span style={{ fontSize: 10, color: S.muted, marginLeft: 5 }}>{r.unit}</span>
                          )}
                        </div>
                        <div style={{
                          fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
                          color: r.certified ? A.sealLit : A.oxideLit,
                        }}>
                          {r.certified ? "CERTIFIED" : "NOT CERTIFIED"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <p style={{
                  marginTop: 28, fontFamily: SANS, fontSize: 13,
                  color: S.muted, lineHeight: 1.75, maxWidth: 650,
                }}>
                  A not-certified outcome means the available evidence did not satisfy the certification
                  criteria. The outcome, supporting diagnostics, and machine-readable reason remain permanently recorded.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
